import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { createTokenRefresh } from './tokenRefresh';

import { OIDC_SCOPE, config } from '@/config';

/**
 * OIDC client for the TimeLog login portal, configured to match the `phoenix-web`
 * client registered in TLP.Login (`IdentityServerConfig.cs`): authorization code
 * flow with PKCE (the oidc-client-ts default), no client secret, refresh tokens.
 *
 * Settings mirror `Web/src/config.ts` so behaviour is consistent across TimeLog SPAs.
 */
export const userManager = new UserManager({
  authority: config.loginPortal,
  client_id: config.clientId,
  redirect_uri: config.redirectUri,
  post_logout_redirect_uri: window.location.origin,
  scope: OIDC_SCOPE,
  // The library's own renewer would call signinSilent() directly, bypassing the
  // single-flight gate below. Duende rotates refresh tokens (OneTimeOnly), so a
  // renewal that races another consumes the token twice → unrecoverable
  // invalid_grant. All renewals must go through `tokenRefresh` instead; the
  // accessTokenExpiring/Expired events still fire with this off.
  automaticSilentRenew: false,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
});

/** The app-wide renewal gate — every token consumer goes through this. */
export const tokenRefresh = createTokenRefresh({
  getUser: () => userManager.getUser(),
  signinSilent: () => userManager.signinSilent(),
  removeUser: () => userManager.removeUser(),
});

// Proactive renewal, replacing automaticSilentRenew but funnelled through the
// single flight. `forceRenew` because the event itself is the expiry signal.
// The expired handler covers background tabs whose throttled timers only fire
// after the token has already lapsed. A renewal raises `userLoaded`, which
// react-oidc-context consumes, so `auth.user` stays current; a fatal failure
// ends in removeUser → the sign-in gate.
userManager.events.addAccessTokenExpiring(() => {
  void tokenRefresh.getFreshAccessToken({ forceRenew: true });
});
userManager.events.addAccessTokenExpired(() => {
  void tokenRefresh.getFreshAccessToken({ forceRenew: true });
});
